import {
  TOTALIZER_ADJUSTMENT_KIND_LABELS,
  TOTALIZER_ADJUSTMENT_STATUS_LABELS,
  TOTALIZER_ADJUSTMENT_STATUSES,
  TOTALIZER_READING_SOURCE_LABELS,
  type TotalizerAdjustment,
  type TotalizerAdjustmentStatus,
  totalizerAdjustmentStatusSchema,
  type TotalizerReadingSource,
  type TotalizerReportRow,
} from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  Drawer,
  EmptyState,
  FormField,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Gauge, Wrench } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApprovalRequestPanel } from "../../../features/approvals/request-panel.js";
import { Field, Section } from "../../../features/expenses/shared.js";
import {
  type AdjustmentNozzleOption,
  TotalizerAdjustmentDialog,
} from "../../../features/fuel/adjustment-dialog.js";
import {
  useFuelPermissions,
  useRefreshFuel,
  useStationAssets,
  useTotalizerAdjustments,
  useTotalizerReadings,
  useTotalizerReport,
} from "../../../features/fuel/queries.js";
import { TotalizerReadingDialog } from "../../../features/fuel/reading-dialogs.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import {
  canCancelAdjustment,
  TOTALIZER_ADJUSTMENT_STATUS_TONE,
  totalizerDifferenceKind,
  totalizerReportTotals,
} from "../../../features/fuel/totalizer-report-values.js";
import { withTotalizerDeltas } from "../../../features/fuel/totalizer-values.js";
import { quickActionSearch, useOpenFromQuickAction } from "../../../lib/hooks.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatCount, formatDate, formatDateTime } from "../../../lib/format.js";

const TABS = ["readings", "report", "adjustments"] as const;
type TotalizerTab = (typeof TABS)[number];

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  nozzle: z.string().uuid().optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  adjStatus: totalizerAdjustmentStatusSchema.optional().catch(undefined),
  adj: z.string().uuid().optional().catch(undefined),
  ...quickActionSearch,
});
type TotalizerSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/akaryakit/endeks")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TotalizersPage,
});

interface NozzleOption {
  id: string;
  label: string;
  currentTotalizer: string;
  productName: string;
}

function TotalizersPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelTotalizers")}
      description={t("fuel.totalizers.description")}
      breadcrumb={[{ label: t("menu.fuelTotalizers") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <TotalizersBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -6), to: today };
}

function TotalizersBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const permissions = useFuelPermissions();
  const station = useFuelStation(companyId);
  const assets = useStationAssets(station.stationId);
  const [readingDialogOpen, setReadingDialogOpen] = useState(false);
  const [adjustmentNozzleId, setAdjustmentNozzleId] = useState<string | null>(null);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const tab: TotalizerTab = search.tab ?? "readings";

  const setSearch = (patch: Partial<TotalizerSearch>) =>
    void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });

  const nozzles: NozzleOption[] = useMemo(
    () =>
      (assets.data?.dispensers ?? []).flatMap((dispenser) =>
        dispenser.nozzles.map((nozzle) => ({
          id: nozzle.id,
          currentTotalizer: nozzle.currentTotalizer,
          productName: nozzle.productName,
          label: t("fuel.totalizers.nozzleLabel", {
            dispenserNo: dispenser.dispenserNo,
            side: nozzle.side,
            nozzleNo: nozzle.nozzleNo,
            product: nozzle.productName,
          }),
        })),
      ),
    [assets.data, t],
  );
  const selected = nozzles.find((nozzle) => nozzle.id === search.nozzle) ?? nozzles[0] ?? null;
  useOpenFromQuickAction(
    search.yeni === "1",
    () => setReadingDialogOpen(true),
    permissions.canCreateReading && selected !== null,
  );

  const openAdjustment = (nozzleId: string | null): void => {
    setAdjustmentNozzleId(nozzleId);
    setAdjustmentOpen(true);
  };

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;
  const stationId = station.stationId;

  return (
    <Tabs
      className="grid gap-4"
      value={tab}
      onValueChange={(next) => setSearch({ tab: next as TotalizerTab })}
    >
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <StationField selection={station} />
        <TabsList aria-label={t("fuel.totalizers.tabs")}>
          {TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`fuel.totalizers.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Card>

      {/* Etkin sekmenin paneli: tetikleyicinin `aria-controls` hedefi var olmalı (axe: aria-valid-attr-value). */}
      <TabsContent value={tab} className="grid gap-4 pt-0">
        {tab === "readings" ? (
          <ReadingsTab
            nozzles={nozzles}
            selected={selected}
            assetsError={assets.error}
            assetsLoaded={Boolean(assets.data)}
            canCreateReading={permissions.canCreateReading}
            canAdjust={permissions.canAdjustTotalizer}
            onSelectNozzle={(nozzleId) => setSearch({ nozzle: nozzleId })}
            onEnterReading={() => setReadingDialogOpen(true)}
            onRequestAdjustment={openAdjustment}
          />
        ) : tab === "report" ? (
          <ReportTab stationId={stationId} search={search} setSearch={setSearch} />
        ) : (
          <AdjustmentsTab
            stationId={stationId}
            nozzles={nozzles}
            search={search}
            setSearch={setSearch}
            canAdjust={permissions.canAdjustTotalizer}
            onRequestAdjustment={openAdjustment}
          />
        )}
      </TabsContent>

      <TotalizerReadingDialog
        nozzle={selected ? { id: selected.id, label: selected.label } : null}
        open={readingDialogOpen}
        onOpenChange={setReadingDialogOpen}
      />
      <TotalizerAdjustmentDialog
        nozzles={nozzles as readonly AdjustmentNozzleOption[]}
        initialNozzleId={adjustmentNozzleId}
        open={adjustmentOpen}
        onOpenChange={setAdjustmentOpen}
      />
    </Tabs>
  );
}

// ── Okumalar ─────────────────────────────────────────────────────────────────

function ReadingsTab({
  nozzles,
  selected,
  assetsError,
  assetsLoaded,
  canCreateReading,
  canAdjust,
  onSelectNozzle,
  onEnterReading,
  onRequestAdjustment,
}: {
  nozzles: NozzleOption[];
  selected: NozzleOption | null;
  assetsError: unknown;
  assetsLoaded: boolean;
  canCreateReading: boolean;
  canAdjust: boolean;
  onSelectNozzle: (nozzleId: string) => void;
  onEnterReading: () => void;
  onRequestAdjustment: (nozzleId: string | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("fuel.totalizers.nozzle")} className="w-72">
          {(control) => (
            <Select
              {...control}
              value={selected?.id ?? ""}
              onChange={(event) => onSelectNozzle(event.target.value)}
            >
              {nozzles.map((nozzle) => (
                <option key={nozzle.id} value={nozzle.id}>
                  {nozzle.label}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {canCreateReading && selected ? (
          <Button variant="outline" onClick={onEnterReading}>
            <Gauge aria-hidden="true" />
            {t("fuel.totalizers.enterReading")}
          </Button>
        ) : null}
      </Card>

      {assetsError ? (
        <Alert tone="danger">{errorMessage(assetsError)}</Alert>
      ) : !assetsLoaded ? (
        <SkeletonRows rows={5} />
      ) : selected === null ? (
        <EmptyState
          icon={<Gauge />}
          title={t("fuel.totalizers.noNozzles")}
          description={t("fuel.totalizers.noNozzlesHint")}
        />
      ) : (
        <ReadingsSection nozzle={selected} canAdjust={canAdjust} onRequestAdjustment={onRequestAdjustment} />
      )}
    </>
  );
}

function ReadingsSection({
  nozzle,
  canAdjust,
  onRequestAdjustment,
}: {
  nozzle: NozzleOption;
  canAdjust: boolean;
  onRequestAdjustment: (nozzleId: string | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const pages = useCursorPages(nozzle.id);
  const readings = useTotalizerReadings(nozzle.id, pages.cursor);
  const rows = useMemo(() => withTotalizerDeltas(readings.data?.items ?? []), [readings.data]);

  return (
    <Section title={t("fuel.totalizers.tableTitle", { nozzle: nozzle.label })}>
      <dl className="grid gap-3 text-sm sm:grid-cols-3">
        <Field label={t("fuel.totalizers.current")}>
          <QuantityText value={nozzle.currentTotalizer} unit="LITRE" />
        </Field>
        <Field label={t("fuel.sales.fields.product")}>{nozzle.productName}</Field>
      </dl>
      <p className="text-xs text-muted-foreground">{t("fuel.totalizers.deltaHint")}</p>

      {readings.error ? (
        <Alert tone="danger">{errorMessage(readings.error)}</Alert>
      ) : !readings.data ? (
        <SkeletonRows rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState title={t("fuel.totalizers.empty")} description={t("fuel.totalizers.emptyHint")} />
      ) : (
        <>
          <div
            className="overflow-x-auto rounded-lg border"
            tabIndex={0}
            role="group"
            aria-label={t("fuel.totalizers.tableLabel")}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t("fuel.totalizers.tableLabel")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("fuel.totalizers.fields.readAt")}</Th>
                  <Th align="right">{t("fuel.totalizers.fields.electronic")}</Th>
                  <Th align="right">{t("fuel.totalizers.fields.mechanical")}</Th>
                  <Th align="right">{t("fuel.totalizers.fields.mechanicalDiff")}</Th>
                  <Th align="right">{t("fuel.totalizers.fields.delta")}</Th>
                  <Th>{t("fuel.totalizers.fields.source")}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.reading.id}
                    className={row.decreasing ? "border-t bg-warning-subtle" : "border-t"}
                  >
                    <td className="px-3 py-2">{formatDateTime(row.reading.readAt)}</td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={row.reading.electronicL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={row.reading.mechanicalL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={row.mechanicalDiffL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="flex flex-wrap items-center justify-end gap-2">
                        <QuantityText value={row.deltaL} unit="LITRE" />
                        {row.decreasing ? (
                          <>
                            <StatusBadge tone="warning">{t("fuel.totalizers.decreasing")}</StatusBadge>
                            {canAdjust ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="min-h-6"
                                onClick={() => onRequestAdjustment(nozzle.id)}
                              >
                                <Wrench aria-hidden="true" />
                                {t("fuel.adjustments.shortcut")}
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {TOTALIZER_READING_SOURCE_LABELS[row.reading.source as TotalizerReadingSource]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className="flex items-center justify-end gap-2" aria-label={t("fuel.totalizers.pagination")}>
            <span className="mr-2 text-sm text-muted-foreground">
              {t("common.pageNumber", { page: pages.pageNumber })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={pages.previous}
              disabled={!pages.hasPrevious || readings.isFetching}
            >
              {t("fuel.totalizers.previousPage")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => pages.next(readings.data?.page.nextCursor)}
              disabled={!readings.data?.page.hasMore || readings.isFetching}
            >
              {t("fuel.totalizers.nextPage")}
            </Button>
          </nav>
        </>
      )}
    </Section>
  );
}

// ── Endeks raporu ────────────────────────────────────────────────────────────

function ReportTab({
  stationId,
  search,
  setSearch,
}: {
  stationId: string;
  search: TotalizerSearch;
  setSearch: (patch: Partial<TotalizerSearch>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };
  const report = useTotalizerReport(stationId, range);
  const rows = useMemo(() => (report.data?.rows ?? []) as TotalizerReportRow[], [report.data]);
  const totals = useMemo(() => totalizerReportTotals(rows), [rows]);

  const columns = useMemo<DataTableColumn<TotalizerReportRow>[]>(
    () => [
      {
        id: "nozzle",
        header: t("fuel.totalizers.report.fields.nozzle"),
        hideable: false,
        exportValue: (row) => row.nozzleLabel,
        cell: (row) => row.nozzleLabel,
      },
      {
        id: "product",
        header: t("fuel.sales.fields.product"),
        exportValue: (row) => row.productName,
        cell: (row) => row.productName,
      },
      {
        id: "firstElectronicL",
        header: t("fuel.totalizers.report.fields.first"),
        align: "right",
        exportValue: (row) => row.firstElectronicL,
        cell: (row) => (
          <span className="grid justify-items-end">
            <QuantityText value={row.firstElectronicL} unit="LITRE" />
            <span className="text-xs text-muted-foreground">{formatDateTime(row.firstReadAt)}</span>
          </span>
        ),
      },
      {
        id: "lastElectronicL",
        header: t("fuel.totalizers.report.fields.last"),
        align: "right",
        exportValue: (row) => row.lastElectronicL,
        cell: (row) => (
          <span className="grid justify-items-end">
            <QuantityText value={row.lastElectronicL} unit="LITRE" />
            <span className="text-xs text-muted-foreground">{formatDateTime(row.lastReadAt)}</span>
          </span>
        ),
      },
      {
        id: "totalizerSalesL",
        header: t("fuel.totalizers.report.fields.totalizerSales"),
        align: "right",
        exportValue: (row) => row.totalizerSalesL,
        cell: (row) => <QuantityText value={row.totalizerSalesL} unit="LITRE" />,
      },
      {
        id: "transactionSalesL",
        header: t("fuel.totalizers.report.fields.transactionSales"),
        align: "right",
        exportValue: (row) => row.transactionSalesL,
        cell: (row) => <QuantityText value={row.transactionSalesL} unit="LITRE" />,
      },
      {
        id: "differenceL",
        header: t("fuel.totalizers.report.fields.difference"),
        align: "right",
        exportValue: (row) => row.differenceL,
        cell: (row) => <DifferenceCell differenceL={row.differenceL} />,
      },
      {
        id: "rollovers",
        header: t("fuel.totalizers.report.fields.rollovers"),
        align: "right",
        exportValue: (row) => row.rollovers,
        cell: (row) => formatCount(row.rollovers),
      },
      {
        id: "anomalies",
        header: t("fuel.totalizers.report.fields.anomalies"),
        align: "right",
        exportValue: (row) => row.anomalies,
        cell: (row) =>
          row.anomalies === 0 ? (
            formatCount(0)
          ) : (
            <StatusBadge tone="warning">{formatCount(row.anomalies)}</StatusBadge>
          ),
      },
      {
        id: "adjustments",
        header: t("fuel.totalizers.report.fields.adjustments"),
        align: "right",
        exportValue: (row) => row.adjustments,
        cell: (row) => formatCount(row.adjustments),
      },
      {
        id: "mechanicalDifferenceL",
        header: t("fuel.totalizers.report.fields.mechanicalDifference"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.mechanicalDifferenceL,
        cell: (row) => <QuantityText value={row.mechanicalDifferenceL} unit="LITRE" />,
      },
      {
        id: "readingCount",
        header: t("fuel.totalizers.report.fields.readingCount"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.readingCount,
        cell: (row) => formatCount(row.readingCount),
      },
    ],
    [t],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("fuel.totalizers.report.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
      </Card>

      <Section title={t("fuel.totalizers.report.totalsTitle")}>
        <p className="text-sm text-muted-foreground">{t("fuel.totalizers.report.totalsHint")}</p>
        <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("fuel.totalizers.report.fields.totalizerSales")}>
            <QuantityText value={totals.totalizerSalesL.toString()} unit="LITRE" />
          </Field>
          <Field label={t("fuel.totalizers.report.fields.transactionSales")}>
            <QuantityText value={totals.transactionSalesL.toString()} unit="LITRE" />
          </Field>
          <Field label={t("fuel.totalizers.report.fields.difference")}>
            <DifferenceCell differenceL={totals.differenceL.toString()} />
          </Field>
          <Field label={t("fuel.totalizers.report.mismatchedNozzles")}>
            {formatCount(totals.mismatchedNozzles)}
          </Field>
          <Field label={t("fuel.totalizers.report.fields.rollovers")}>{formatCount(totals.rollovers)}</Field>
          <Field label={t("fuel.totalizers.report.fields.anomalies")}>{formatCount(totals.anomalies)}</Field>
          <Field label={t("fuel.totalizers.report.fields.adjustments")}>
            {formatCount(totals.adjustments)}
          </Field>
          <Field label={t("fuel.totalizers.report.fields.readingCount")}>
            {formatCount(totals.readingCount)}
          </Field>
        </dl>
      </Section>

      <DataTable<TotalizerReportRow>
        caption={t("fuel.totalizers.report.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.nozzleId}
        loading={report.isPending}
        error={report.error ? <Alert tone="danger">{errorMessage(report.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Gauge />}
            title={t("fuel.totalizers.report.emptyTitle")}
            description={t("fuel.totalizers.report.emptyDescription")}
          />
        }
        onExport={async (visibleColumns) => {
          await exportRowsToExcel({
            fileName: t("fuel.totalizers.report.exportFileName", {
              from: formatDate(range.from),
              to: formatDate(range.to),
            }),
            sheetName: t("fuel.totalizers.tab.report"),
            columns: visibleColumns,
            rows,
          });
        }}
      />
    </>
  );
}

/** Fark: renk TEK taşıyıcı değildir — yönü metin rozeti de söyler. */
function DifferenceCell({ differenceL }: { differenceL: string }): ReactElement {
  const { t } = useTranslation();
  const kind = totalizerDifferenceKind(differenceL);
  return (
    <span className="flex flex-wrap items-center justify-end gap-2">
      <QuantityText value={differenceL} unit="LITRE" />
      {kind === "none" ? null : (
        <StatusBadge tone="warning">{t(`fuel.totalizers.report.difference.${kind}`)}</StatusBadge>
      )}
    </span>
  );
}

// ── Düzeltmeler ──────────────────────────────────────────────────────────────

function AdjustmentsTab({
  stationId,
  nozzles,
  search,
  setSearch,
  canAdjust,
  onRequestAdjustment,
}: {
  stationId: string;
  nozzles: NozzleOption[];
  search: TotalizerSearch;
  setSearch: (patch: Partial<TotalizerSearch>) => void;
  canAdjust: boolean;
  onRequestAdjustment: (nozzleId: string | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  const filters = { status: search.adjStatus };
  const pages = useCursorPages(`${stationId}:${search.adjStatus ?? "all"}`);
  const list = useTotalizerAdjustments(stationId, filters, pages.cursor);
  const rows = useMemo(() => (list.data?.items ?? []) as TotalizerAdjustment[], [list.data]);
  const selected = rows.find((row) => row.id === search.adj) ?? null;

  const columns = useMemo<DataTableColumn<TotalizerAdjustment>[]>(
    () => [
      {
        id: "effectiveAt",
        header: t("fuel.adjustments.fields.effectiveAt"),
        hideable: false,
        exportValue: (row) => formatDateTime(row.effectiveAt),
        cell: (row) => formatDateTime(row.effectiveAt),
      },
      {
        id: "nozzle",
        header: t("fuel.totalizers.nozzle"),
        exportValue: (row) => `${row.nozzleLabel} · ${row.productName}`,
        cell: (row) => `${row.nozzleLabel} · ${row.productName}`,
      },
      {
        id: "kind",
        header: t("fuel.adjustments.fields.kind"),
        exportValue: (row) => TOTALIZER_ADJUSTMENT_KIND_LABELS[row.kind],
        cell: (row) => TOTALIZER_ADJUSTMENT_KIND_LABELS[row.kind],
      },
      {
        id: "previousElectronicL",
        header: t("fuel.adjustments.fields.previousElectronic"),
        align: "right",
        exportValue: (row) => row.previousElectronicL,
        cell: (row) => <QuantityText value={row.previousElectronicL} unit="LITRE" />,
      },
      {
        id: "newElectronicL",
        header: t("fuel.adjustments.fields.newElectronic"),
        align: "right",
        exportValue: (row) => row.newElectronicL,
        cell: (row) => <QuantityText value={row.newElectronicL} unit="LITRE" />,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => TOTALIZER_ADJUSTMENT_STATUS_LABELS[row.status],
        cell: (row) => (
          <StatusBadge tone={TOTALIZER_ADJUSTMENT_STATUS_TONE[row.status]}>
            {TOTALIZER_ADJUSTMENT_STATUS_LABELS[row.status]}
          </StatusBadge>
        ),
      },
      {
        id: "reason",
        header: t("fuel.adjustments.fields.reason"),
        defaultVisible: false,
        exportValue: (row) => row.reason,
        cell: (row) => row.reason,
      },
    ],
    [t],
  );

  return (
    <>
      <DataTable<TotalizerAdjustment>
        caption={t("fuel.adjustments.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Wrench />}
            title={t("fuel.adjustments.emptyTitle")}
            description={t("fuel.adjustments.emptyDescription")}
          />
        }
        onRowClick={(row) => setSearch({ adj: row.id })}
        toolbar={
          canAdjust ? (
            <Button variant="outline" onClick={() => onRequestAdjustment(nozzles[0]?.id ?? null)}>
              <Wrench aria-hidden="true" />
              {t("fuel.adjustments.newRequest")}
            </Button>
          ) : undefined
        }
        filters={
          <FormField label={t("common.status")} className="w-52">
            {(control) => (
              <Select
                {...control}
                value={search.adjStatus ?? ""}
                onChange={(event) =>
                  setSearch({
                    adjStatus: (event.target.value || undefined) as TotalizerAdjustmentStatus | undefined,
                    adj: undefined,
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {TOTALIZER_ADJUSTMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {TOTALIZER_ADJUSTMENT_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <p className="text-xs text-muted-foreground">{t("fuel.adjustments.rowHint")}</p>

      <AdjustmentDrawer item={selected} onClose={() => setSearch({ adj: undefined })} />
    </>
  );
}

function AdjustmentDrawer({
  item,
  onClose,
}: {
  item: TotalizerAdjustment | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const [error, setError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/fuel/totalizer-adjustments/{id}/cancel", {
          params: { path: { id: item!.id } },
          body: { version: item!.version },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("fuel.adjustments.cancelled") });
      onClose();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Drawer
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) {
          setError(null);
          onClose();
        }
      }}
      title={item ? t("fuel.adjustments.detailTitle", { nozzle: item.nozzleLabel }) : ""}
    >
      {item === null ? null : (
        <div className="grid gap-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <p>
            <StatusBadge tone={TOTALIZER_ADJUSTMENT_STATUS_TONE[item.status]}>
              {TOTALIZER_ADJUSTMENT_STATUS_LABELS[item.status]}
            </StatusBadge>
          </p>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Field label={t("fuel.totalizers.nozzle")}>
              {item.nozzleLabel} · {item.productName}
            </Field>
            <Field label={t("fuel.adjustments.fields.kind")}>
              {TOTALIZER_ADJUSTMENT_KIND_LABELS[item.kind]}
            </Field>
            <Field label={t("fuel.adjustments.fields.effectiveAt")}>{formatDateTime(item.effectiveAt)}</Field>
            <Field label={t("fuel.adjustments.fields.createdAt")}>{formatDateTime(item.createdAt)}</Field>
            <Field label={t("fuel.adjustments.fields.previousElectronic")}>
              <QuantityText value={item.previousElectronicL} unit="LITRE" />
            </Field>
            <Field label={t("fuel.adjustments.fields.newElectronic")}>
              <QuantityText value={item.newElectronicL} unit="LITRE" />
            </Field>
            <Field label={t("fuel.adjustments.fields.newMechanical")}>
              <QuantityText value={item.newMechanicalL} unit="LITRE" />
            </Field>
            <Field label={t("fuel.adjustments.fields.decidedAt")}>{formatDateTime(item.decidedAt)}</Field>
            <Field label={t("fuel.adjustments.fields.reason")}>{item.reason}</Field>
          </dl>

          {permissions.canAdjustTotalizer && canCancelAdjustment(item.status) ? (
            <div className="flex justify-end">
              <Button variant="outline" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                {t("fuel.adjustments.cancel")}
              </Button>
            </div>
          ) : null}

          {item.approvalRequestId ? (
            <ApprovalRequestPanel requestId={item.approvalRequestId} showSummary={false} />
          ) : (
            <Alert tone="info">{t("fuel.adjustments.noApprovalRequest")}</Alert>
          )}
        </div>
      )}
    </Drawer>
  );
}
